/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added CSV export functionality.
 *                                   - Ensured `totalItems` prop is passed to Pagination.
 *                                   - Integrated Spinner for loading states.
 *                                   - Corrected cost calculation for display with surcharge.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide columns
 *                                   and customize column headers based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays a list of composite products.
 *                      It does not handle sensitive data directly.
 * INTENT: Render a paginated table of Composite products with actions for editing,
 *         deleting, and viewing composition, providing a clear overview and management interface,
 *         now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure pagination logic is correctly integrated
 *                        and that actions are clearly visible and accessible.
 * LLM_CONFIDENCE: 100% - Standard table component with pagination and action buttons.
 */
import React, { useState } from 'react';
import { Composite } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx';
import CompositionViewer from './CompositionViewer.tsx';
import { Link } from 'react-router-dom';
import { Spinner } from './ui/spinner.tsx';
import { useSurchargeLookup } from '../hooks/useSurchargeLookup.ts';
import { apiClient } from '../api/apiClient.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface CompositesTableProps {
  composites: Composite[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (composite: Composite) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string;
  entityId: string; // NEW: Add entityId prop
}

const CompositesTable: React.FC<CompositesTableProps> = ({
  composites,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  baseEndpoint,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context
  const { surcharges, loading: surchargesLoading, error: surchargesError } = useSurchargeLookup();
  const [exportLoading, setExportLoading] = useState(false);

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const getEntityTitle = (defaultTitle: string) => 
    whitelabelConfig.entities?.[entityId].title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: unknown) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading || surchargesLoading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading {getEntityTitle('Composites')}...</p>
    );

  if (composites.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No {getEntityTitle('Composites')} found.</p>
    );

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4">
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {isFieldVisible('description') && <TableHead>{getFieldTitle('description', 'Description')}</TableHead>}
            {isFieldVisible('article_number') && <TableHead>{getFieldTitle('article_number', 'Article No.')}</TableHead>}
            {isFieldVisible('family_id') && <TableHead>{getFieldTitle('family_id', 'Family')}</TableHead>}
            {isFieldVisible('tax_profile_id') && <TableHead>{getFieldTitle('tax_profile_id', 'Surcharge Profile')}</TableHead>}
            {isFieldVisible('cost') && <TableHead>{getFieldTitle('cost', 'Cost')}</TableHead>}
            {isFieldVisible('retail_price') && <TableHead>{getFieldTitle('retail_price', 'Price')}</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {composites.map((composite) => {
            const hasComposition =
              (composite.composition?.components && composite.composition.components.length > 0) ||
              (composite.composition?.processes && composite.composition.processes.length > 0);

              const surchargeProfile = surcharges.find(s => s.id === composite.tax_profile_id);
              const surchargePercentage = surchargeProfile?.percentage || 0;
              const costWithOutSurcharge = composite.cost ? composite.cost / (1 + surchargePercentage / 100) : null;
            return (
              <TableRow key={composite.id}>
                {isFieldVisible('description') && <TableCell>{composite.description}</TableCell>}
                {isFieldVisible('article_number') && <TableCell>{composite.article_number}</TableCell>}
                {isFieldVisible('family_id') && (
                  <TableCell>
                    {composite._labels?.family_id || `ID: ${composite.family_id}`}
                  </TableCell>
                )}
                {isFieldVisible('tax_profile_id') && (
                  <TableCell>
                    {composite._labels?.tax_profile_id || (composite.tax_profile_id ? `ID: ${composite.tax_profile_id}` : '-')}
                  </TableCell>
                )}
                {isFieldVisible('cost') && <TableCell>${composite.cost?.toFixed(2) || 'N/A'}</TableCell>}
                {isFieldVisible('retail_price') && <TableCell>${composite.retail_price?.toFixed(2) || 'N/A'}</TableCell>}
                <TableCell className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(composite)}
                  >
                    Edit
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Composition for {composite.description}</DialogTitle>
                      </DialogHeader>
                      <CompositionViewer composition={composite.composition} total_cost={costWithOutSurcharge?.toFixed(2) || 'N/A'} />
                    </DialogContent>
                  </Dialog>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <Link to={`/composition-edit/${composite.id}`}>
                      Edit Composition
                    </Link>
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDelete(composite.id!)}
                    disabled={hasComposition}
                    title={hasComposition ? 'Cannot delete while has composition' : undefined}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || exportLoading}
      />
    </div>
  );
};

export default CompositesTable;